import { useEffect, useState } from "react";
import { CrmLayout } from "@/components/crm/CrmLayout";
import { api, apiError } from "@/lib/api";
import { useConfig } from "@/context/ConfigContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { FlaskConical } from "lucide-react";

export default function Settings() {
  const { config, refresh } = useConfig();
  const [form, setForm] = useState(config);
  useEffect(() => { setForm(config); }, [config]);

  const save = async () => {
    try {
      await api.patch("/crm/config", {
        brand_name: form.brand_name, logo_url: form.logo_url, primary_color: form.primary_color,
        secondary_color: form.secondary_color, contact_email: form.contact_email, contact_phone: form.contact_phone,
        address: form.address, orias_number: form.orias_number, legal_entity: form.legal_entity,
        demo_mode: !!form.demo_mode,
      });
      toast.success("Paramètres enregistrés."); refresh();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const F = ({ k, label, ph }) => (
    <div><Label className="mb-1.5 block">{label}</Label><Input value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} placeholder={ph} data-testid={`config-${k}`} /></div>
  );

  return (
    <CrmLayout>
      <h1 className="font-display text-2xl font-bold text-slate-900">Paramètres de la marque</h1>
      <p className="text-slate-500 text-sm mt-1">Nom, logo, couleurs et informations légales du cabinet (configurables). Ne jamais inventer de références réglementaires.</p>

      <div className="grid lg:grid-cols-2 gap-5 mt-6">
        <Card className="p-6 space-y-4">
          <h3 className="font-semibold text-slate-900">Identité</h3>
          <F k="brand_name" label="Nom de la marque" />
          <F k="logo_url" label="URL du logo" ph="https://" />
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="mb-1.5 block">Couleur principale</Label><div className="flex gap-2"><Input type="color" value={form.primary_color || "#1E40AF"} onChange={(e) => setForm({ ...form, primary_color: e.target.value })} className="w-14 p-1" /><Input value={form.primary_color || ""} onChange={(e) => setForm({ ...form, primary_color: e.target.value })} /></div></div>
            <div><Label className="mb-1.5 block">Couleur secondaire</Label><div className="flex gap-2"><Input type="color" value={form.secondary_color || "#0B192C"} onChange={(e) => setForm({ ...form, secondary_color: e.target.value })} className="w-14 p-1" /><Input value={form.secondary_color || ""} onChange={(e) => setForm({ ...form, secondary_color: e.target.value })} /></div></div>
          </div>
        </Card>
        <Card className="p-6 space-y-4">
          <h3 className="font-semibold text-slate-900">Coordonnées & légal</h3>
          <F k="legal_entity" label="Raison sociale (entité juridique)" />
          <F k="orias_number" label="Numéro ORIAS" ph="Ex. 12 345 678" />
          <F k="contact_email" label="E-mail de contact" />
          <F k="contact_phone" label="Téléphone" />
          <F k="address" label="Adresse" />
        </Card>
      </div>
      <Button className="mt-6" onClick={save} data-testid="save-config">Enregistrer les paramètres</Button>

      <Card className="p-6 mt-6 border-indigo-200 bg-indigo-50/40">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3">
            <span className="w-10 h-10 rounded-xl bg-indigo-600 flex items-center justify-center shrink-0"><FlaskConical className="text-white" size={20} /></span>
            <div>
              <h3 className="font-semibold text-slate-900">Mode démonstration</h3>
              <p className="text-sm text-slate-500 mt-1 max-w-xl">Affiche des <strong>tarifs d'exemple</strong> sur la page de résultats, clairement étiquetés « Tarifs de démonstration — non contractuels ». À utiliser pour présenter le futur comparateur à un client. Aucun tarif réel n'est généré.</p>
            </div>
          </div>
          <Switch checked={!!form.demo_mode} onCheckedChange={(v) => setForm({ ...form, demo_mode: v })} data-testid="toggle-demo-mode" />
        </div>
        <Button className="mt-5" onClick={save} data-testid="save-demo-mode">Enregistrer</Button>
      </Card>
    </CrmLayout>
  );
}
